import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import EnumTableCommon from "../../common/EnumTable/EnumTableCommon";
import { createEmptyEventEnum, setEventTypeExtraProperties } from "../../../utils/pdApiUtils";
import { parseUrlToName, parseNameToUrl } from "../../../utils/utils";
import { useParams, Redirect } from "react-router";
import { Filter } from "../../../components/Table/Migration/LegacyTypes";
import NavigationLink from "../../../components/NavigationLink/NavigationLink";
import ActionMenu from "../../common/ActionMenu/ActionMenu";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { editActions } from "../../../actions/editActions";
import ContentLayout from "../../../components/ContentLayout/ContentLayout";

interface EscalatorEventEnumerationsProps {
  objectIdentifier: string;
}

const EscalatorEventEnumerations: React.FC<EscalatorEventEnumerationsProps> = props => {
  const subPages: Internal.EscalatorEnumSubPages = {
    "Escalator Event Classes": {
      itemName: "event class", //AddEventClass button in UI is this only.
      PdApiField: "EscalatorEventClasses",
      dataView: EnumTableCommon,
      createEmpty: createEmptyEventEnum,
      setExtraProperties: null
    },
    "Escalator Event Types": {
      itemName: "event type",
      PdApiField: "EscalatorEventTypes",
      dataView: EnumTableCommon,
      createEmpty: createEmptyEventEnum,
      setExtraProperties: setEventTypeExtraProperties
    },
    "Escalator Event Pids": {
      itemName: "event pid",
      PdApiField: "EscalatorEventPids",
      dataView: EnumTableCommon,
      createEmpty: createEmptyEventEnum,
      setExtraProperties: null
    },
    "Escalator Ext. Commands": {
      itemName: "command",
      PdApiField: "EscalatorExtCommands",
      dataView: EnumTableCommon,
      createEmpty: createEmptyEventEnum,
      setExtraProperties: null
    },
    "Escalator Request Types": {
      itemName: "request type",
      PdApiField: "EscalatorRequestTypes",
      dataView: EnumTableCommon,
      createEmpty: createEmptyEventEnum,
      setExtraProperties: null
    }
  };

  // Get activeMenu from URL params
  const { activeMenu: activeMenuParam } = useParams<{ activeMenu?: string }>();
  const dispatch = useDispatch();
  const filterOptions = useSelector((state: State.Root) => state.filterOptions);

  // Parse URL param or set default value
  const initialActiveMenu: State.EscalatorEventEnumsSubmenu = activeMenuParam
    ? (parseUrlToName(activeMenuParam) as State.EscalatorEventEnumsSubmenu)
    : "Escalator Event Classes";

  // Define state using useState
  const [activeMenu, setActiveMenu] = useState<State.EscalatorEventEnumsSubmenu>(initialActiveMenu);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableColumnFilters, setTableColumnFilters] = useState<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Record<State.EscalatorEventEnumsSubmenu, any[]>
  >({
    "Escalator Event Classes": [],
    "Escalator Event Types": [],
    "Escalator Event Pids": [],
    "Escalator Ext. Commands": [],
    "Escalator Request Types": []
  });

  useEffect(() => {
    if (activeMenu !== initialActiveMenu) {
      setActiveMenu(initialActiveMenu);
    }
  }, [initialActiveMenu, activeMenu]);

  if (activeMenuParam === undefined) {
    return <Redirect to="/escalator/event-enumerations/escalator-event-classes" />;
  }

  const setColumnFilters = (
    subMenuRoute: State.EscalatorEventEnumsSubmenu,
    newFilters: Filter[]
  ) => {
    setTableColumnFilters(prevFilters => ({
      ...prevFilters,
      [subMenuRoute]: newFilters
    }));
  };

  const toggleModal = (): void => {
    setIsModalOpen(prevState => !prevState);
  };

  const links = Object.keys(subPages).map(name => (
    <NavigationLink
      key={name}
      to={"/escalator/event-enumerations/" + parseNameToUrl(name)}
      name={name}
      title={name.toString().replace(/^Escalator\s*/, "")}
    />
  ));

  const actionMenu = (
    <ActionMenu
      {...props}
      subMenuRoute={activeMenu}
      itemName={subPages[activeMenu].itemName}
      selectedColumnsName={activeMenu}
      dataFieldName={subPages[activeMenu].PdApiField}
      setExtraProperties={subPages[activeMenu].setExtraProperties}
      createEmpty={subPages[activeMenu].createEmpty}
      groupName="Escalator"
      columnFilters={tableColumnFilters[activeMenu]}
      filterOptions={filterOptions}
    />
  );

  const dataView = (
    <EnumTableCommon
      subMenuRoute={activeMenu}
      toggleModal={toggleModal}
      dataFieldName={subPages[activeMenu].PdApiField}
      setColumnFilters={setColumnFilters}
    />
  );

  const buildModal = () => {
    const content = (
      <span>
        Are you sure you want to delete this {subPages[activeMenu].itemName}?
        <br />
        {props.objectIdentifier}
      </span>
    );
    return (
      <ConfirmationModal
        isOpen={isModalOpen}
        size="mini"
        header="Confirm delete"
        content={content}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={() =>
          dispatch(editActions.delete(subPages[activeMenu].PdApiField as Api.OptionsGrud))
        }
        onCancel={toggleModal}
      />
    );
  };

  return (
    <div>
      {" "}
      <>
        <ContentLayout submenu={links} actionMenu={actionMenu} dataView={dataView} />
        {buildModal()}
      </>
    </div>
  );
};

export default EscalatorEventEnumerations;
